import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import {
    DeleteOutlined,
    DownOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
    LoadingOutlined,
    PlusOutlined,
    QuestionOutlined
} from '@ant-design/icons';
import { Button, Card, Col, Modal, Row, Select, Space, Table, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';

import AddModal from './AddModal';
import EditModal from './EditModal';
import styles from './Staff.module.scss';
import { isAccessEnded } from '../../../../helpers/utils/functions';
import MainLayout from '../../../../shared/common/components/layouts/MainLayout';
import { useActions } from '../../../../shared/common/hooks/useActions';
import { useTypedSelector } from '../../../../shared/common/hooks/useTypedSelector';
import { IStaffData } from '../../../../app/providers/redux/slices/staff/types';

const { Text, Title } = Typography;

const MAX_VISIBLE_ITEMS = 2;

interface IStatsItem {
    label: string;
    count: number;
}

const renderCellWithLimit = (value: string | undefined, cellStyles?: string) => {
    if (!value) return null;
    
    const items = value.split(',').map(item => item.trim()).filter(Boolean);
    
    // Если много элементов через запятую - показываем ограниченное количество
    if (items.length > MAX_VISIBLE_ITEMS) {
        const visibleItems = items.slice(0, MAX_VISIBLE_ITEMS);
        const hiddenCount = items.length - MAX_VISIBLE_ITEMS;
        
        return (
            <Tooltip 
                title={
                    <div style={{ maxWidth: 'none', whiteSpace: 'nowrap' }}>
                        {items.map((item, idx) => <div key={idx}>{item}</div>)}
                    </div>
                }
                placement="top"
                overlayStyle={{ maxWidth: 'none' }}
                overlayInnerStyle={{ maxWidth: 'none' }}
            >
                <span>
                    {visibleItems.join(', ')}
                    <span style={{ color: '#1890ff', cursor: 'pointer', marginLeft: 4 }}>
                        +{hiddenCount}
                    </span>
                </span>
            </Tooltip>
        );
    }
    
    // Для длинного текста без запятых - обрезаем по строкам с тултипом
    return (
        <Tooltip 
            title={value}
            placement="top"
            overlayStyle={{ maxWidth: 400 }}
        >
            <div className={cellStyles}>
                {value}
            </div>
        </Tooltip>
    );
};


interface IFilters {
    name: string;
    email: string;
    role: string;
    utm_source: string;
    utm_medium: string;
    utm_campaign: string;
    utm_term: string;
    utm_content: string;
    utm_group: string;
    ads: string;
    offers: string;
    contact: string;
    setting: string;
}

const initialFilters: IFilters = {
    name: '',
    email: '',
    role: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
    utm_group: '',
    ads: '',
    offers: '',
    contact: '',
    setting: '',
};

const Staff: FC = () => {

    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
    const { isAboutReportOpen } = useTypedSelector((state) => state.app);

    const { isLoading, isUpdate, staffData: rawStaffData } = useTypedSelector((state) => state.staff);
    const staffData = Array.isArray(rawStaffData) ? rawStaffData : [];
    const { staffGetData, staffSetUpdate, staffDeleteUser, setAppReportOpen, setDrawerLink } = useActions();

    const [addModalVisible, setAddModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editUserId, setEditUserId] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState<IFilters>(initialFilters);
    const [expandedBlocks, setExpandedBlocks] = useState<Record<string, boolean>>({});
    const filtersRef = useRef<HTMLDivElement>(null);

    const scrollToFilters = () => {
        setTimeout(() => {
            filtersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    const toggleBlockExpand = (blockId: string) => {
        setExpandedBlocks(prev => ({ ...prev, [blockId]: !prev[blockId] }));
    };

    const MAX_BLOCK_ITEMS = 10;

    // Компонент для рендеринга сворачиваемого списка
    const renderCollapsibleList = (items: IStatsItem[], blockId: string, filterField?: keyof IFilters) => {
        const isExpanded = expandedBlocks[blockId];
        const needsCollapse = items.length > MAX_BLOCK_ITEMS;
        const displayItems = needsCollapse && !isExpanded ? items.slice(0, MAX_BLOCK_ITEMS) : items;

        const handleItemClick = (label: string) => {
            if (filterField && label !== 'Не указано') {
                resetFilters();
                setShowFilters(true);
                handleFilterChange(filterField, label);
                scrollToFilters();
            }
        };

        const isClickable = (label: string) => filterField && label !== 'Не указано';

        return (
            <>
                <div className={needsCollapse && !isExpanded ? styles.statListCollapsible : styles.statList}>
                    {displayItems.map((item, idx) => (
                        <div key={idx} className={styles.statListItem}>
                            <span className={styles.statItemLabel}>{item.label}</span>
                            <span 
                                className={`${styles.statBadge} ${isClickable(item.label) ? styles.statBadgeClickable : ''}`}
                                onClick={() => handleItemClick(item.label)}
                                style={isClickable(item.label) ? { cursor: 'pointer' } : undefined}
                            >
                                {item.count}
                            </span>
                        </div>
                    ))}
                </div>
                {needsCollapse && (
                    <div 
                        className={`${styles.expandButton} ${isExpanded ? styles.expanded : ''}`}
                        onClick={() => toggleBlockExpand(blockId)}
                    >
                        {isExpanded ? 'Свернуть' : `Показать ещё ${items.length - MAX_BLOCK_ITEMS}`}
                        <DownOutlined />
                    </div>
                )}
            </>
        );
    };

    const handleFilterChange = (field: keyof IFilters, value: string) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const resetFilters = () => {
        setFilters(initialFilters);
    };

    // Получаем уникальные значения для каждого поля
    const uniqueValues = useMemo(() => {
        const getUniqueValues = (field: keyof IStaffData): string[] => {
            const values = staffData
                .map((item: IStaffData) => item[field])
                .filter((value): value is string => Boolean(value) && value !== '');
            return [...new Set(values)].sort();
        };

        // Для рекламных кабинетов разбиваем по запятой и собираем уникальные
        const getUniqueAds = (): string[] => {
            const allAds: string[] = [];
            staffData.forEach((item: IStaffData) => {
                if (item.ads && item.ads.trim() !== '') {
                    const adsList = item.ads.split(',').map(ad => ad.trim()).filter(Boolean);
                    allAds.push(...adsList);
                }
            });
            return [...new Set(allAds)].sort();
        };

        // Для предложений тоже разбиваем по запятой
        const getUniqueOffers = (): string[] => {
            const allOffers: string[] = [];
            staffData.forEach((item: IStaffData) => {
                if (item.offers && item.offers.trim() !== '') {
                    const offersList = item.offers.split(',').map(o => o.trim()).filter(Boolean);
                    allOffers.push(...offersList);
                }
            });
            return [...new Set(allOffers)].sort();
        };

        // Для UTM меток разбиваем по запятой и собираем уникальные
        const getUniqueUtm = (field: keyof IStaffData): string[] => {
            const allValues: string[] = [];
            staffData.forEach((item: IStaffData) => {
                const value = item[field];
                if (value && typeof value === 'string' && value.trim() !== '' && value !== 'Доступны все метки') {
                    const valuesList = value.split(',').map(v => v.trim()).filter(Boolean);
                    allValues.push(...valuesList);
                }
            });
            return [...new Set(allValues)].sort();
        };

        return {
            name: getUniqueValues('name'),
            email: getUniqueValues('email'),
            role: getUniqueValues('role'),
            utm_source: getUniqueUtm('utm_source'),
            utm_medium: getUniqueUtm('utm_medium'),
            utm_campaign: getUniqueUtm('utm_campaign'),
            utm_term: getUniqueUtm('utm_term'),
            utm_content: getUniqueUtm('utm_content'),
            utm_group: getUniqueUtm('utm_group'),
            ads: getUniqueAds(),
            offers: getUniqueOffers(),
            contact: getUniqueValues('contact'),
            setting: (staffData as any[])
                .map((item) => item.setting)
                .filter((value): value is string => Boolean(value) && value !== '')
                .filter((value, index, self) => self.indexOf(value) === index)
                .sort(),
        };
    }, [staffData]);

    const filteredData = useMemo(() => {
        return staffData.filter((item: IStaffData) => {
            const matchName = !filters.name || item.name === filters.name;
            const matchEmail = !filters.email || item.email === filters.email;
            const matchRole = !filters.role || item.role === filters.role;
            // Для UTM меток проверяем, содержит ли поле выбранное значение
            const matchUtmSource = !filters.utm_source || (item.utm_source && item.utm_source.split(',').map(v => v.trim()).includes(filters.utm_source));
            const matchUtmMedium = !filters.utm_medium || (item.utm_medium && item.utm_medium.split(',').map(v => v.trim()).includes(filters.utm_medium));
            const matchUtmCampaign = !filters.utm_campaign || (item.utm_campaign && item.utm_campaign.split(',').map(v => v.trim()).includes(filters.utm_campaign));
            const matchUtmTerm = !filters.utm_term || (item.utm_term && item.utm_term.split(',').map(v => v.trim()).includes(filters.utm_term));
            const matchUtmContent = !filters.utm_content || (item.utm_content && item.utm_content.split(',').map(v => v.trim()).includes(filters.utm_content));
            const matchUtmGroup = !filters.utm_group || (item.utm_group && item.utm_group.split(',').map(v => v.trim()).includes(filters.utm_group));
            // Для ads и offers проверяем, содержит ли поле выбранное значение
            const matchAds = !filters.ads || (item.ads && item.ads.split(',').map(a => a.trim()).includes(filters.ads));
            const matchOffers = !filters.offers || (item.offers && item.offers.split(',').map(o => o.trim()).includes(filters.offers));
            const matchContact = !filters.contact || item.contact === filters.contact;
            const matchSetting = !filters.setting || (item as any).setting === filters.setting;

            return matchName && matchEmail && matchRole && matchUtmSource && 
                   matchUtmMedium && matchUtmCampaign && matchUtmTerm && matchUtmContent && 
                   matchUtmGroup && matchAds && matchOffers && matchContact && matchSetting;
        });
    }, [staffData, filters]);

    // Статистика для дашборда
    const dashboardStats = useMemo(() => {
        const totalStaff = staffData.length;

        // Распределение по ролям (Чем занимаются)
        const roleStats: IStatsItem[] = [];
        const roleCount: Record<string, number> = {};
        let noRoleCount = 0;
        staffData.forEach((item: IStaffData) => {
            if (item.role && item.role.trim() !== '') {
                roleCount[item.role] = (roleCount[item.role] || 0) + 1;
            } else {
                noRoleCount++;
            }
        });
        Object.entries(roleCount).forEach(([label, count]) => {
            roleStats.push({ label, count });
        });
        roleStats.sort((a, b) => b.count - a.count);
        if (noRoleCount > 0) {
            roleStats.push({ label: 'Не указано', count: noRoleCount });
        }

        // Распределение по значениям UTM меток — по каждой метке отдельно
        const getUtmValueStats = (field: keyof IStaffData): IStatsItem[] => {
            const valueCount: Record<string, number> = {};
            staffData.forEach((item: IStaffData) => {
                const value = item[field];
                if (value && typeof value === 'string' && value !== 'Доступны все метки') {
                    const values = value.split(',').map(v => v.trim()).filter(Boolean);
                    values.forEach(v => {
                        valueCount[v] = (valueCount[v] || 0) + 1;
                    });
                }
            });
            return Object.entries(valueCount)
                .map(([label, count]) => ({ label, count }))
                .sort((a, b) => b.count - a.count);
        };

        const utmSourceStats = getUtmValueStats('utm_source');
        const utmMediumStats = getUtmValueStats('utm_medium');
        const utmCampaignStats = getUtmValueStats('utm_campaign');
        const utmTermStats = getUtmValueStats('utm_term');
        const utmContentStats = getUtmValueStats('utm_content');
        const utmGroupStats = getUtmValueStats('utm_group');
        
        let allUtmAccess = 0;
        let utmRestricted = 0;
        staffData.forEach((item: IStaffData) => {
            const hasRestriction = 
                (item.utm_source && item.utm_source !== 'Доступны все метки') ||
                (item.utm_medium && item.utm_medium !== 'Доступны все метки') ||
                (item.utm_campaign && item.utm_campaign !== 'Доступны все метки') ||
                (item.utm_term && item.utm_term !== 'Доступны все метки') ||
                (item.utm_content && item.utm_content !== 'Доступны все метки') ||
                (item.utm_group && item.utm_group !== 'Доступны все метки');
            if (!hasRestriction) {
                allUtmAccess++;
            } else {
                utmRestricted++;
            }
        });

        // Доступ к рекламным кабинетам (по каждому кабинету)
        const adsCount: Record<string, number> = {};
        let noAdsAccess = 0;
        let allAdsAccess = 0;
        
        staffData.forEach((item: IStaffData) => {
            if (item.ads && item.ads.trim() !== '') {
                const adsList = item.ads.split(',').map(ad => ad.trim()).filter(Boolean);
                // Проверяем, есть ли "Доступны все РК" среди значений
                if (adsList.some(ad => ad === 'Доступны все РК' || ad.toLowerCase().includes('доступны все рк'))) {
                    allAdsAccess++;
                }
                // Проверяем, есть ли "Нет доступа к РК" среди значений
                if (item.ads.trim() === 'Нет доступа к РК') {
                    noAdsAccess++;
                }
                adsList.forEach(ad => {
                    adsCount[ad] = (adsCount[ad] || 0) + 1;
                });
            }
        });
        
        const adsStats: IStatsItem[] = Object.entries(adsCount)
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count);

        // Доступны все предложения
        let allOffersAccess = 0;
        staffData.forEach((item: IStaffData) => {
            if (item.offers && (item.offers.trim() === 'Доступны все предложения' || item.offers.toLowerCase().includes('доступны все предложения'))) {
                allOffersAccess++;
            }
        });

        // Доступ к контактам пользователей
        const contactStats = {
            withContact: 0,
            withoutContact: 0,
        };
        staffData.forEach((item: IStaffData) => {
            // Контакты доступны если значение точно равно "Доступны"
            if (item.contact && item.contact.trim() === 'Доступны') {
                contactStats.withContact++;
            }
            // Контакты не доступны если значение точно равно "Не доступны"
            if (item.contact && item.contact.trim() === 'Не доступны') {
                contactStats.withoutContact++;
            }
        });

        // Администраторы (видят настройки) - у кого в поле setting указано точно "Доступны"
        const adminStats = {
            admins: 0,
            notAdmins: 0,
        };
        staffData.forEach((item: any) => {
            if (item.setting && item.setting.trim() === 'Доступны') {
                adminStats.admins++;
            } else {
                adminStats.notAdmins++;
            }
        });

        return {
            totalStaff,
            roleStats,
            utmSourceStats,
            utmMediumStats,
            utmCampaignStats,
            utmTermStats,
            utmContentStats,
            utmGroupStats,
            allUtmAccess,
            utmRestricted,
            adsStats,
            noAdsAccess,
            allAdsAccess,
            allOffersAccess,
            contactStats,
            adminStats,
        };
    }, [staffData]);

    useEffect(() => {
        if (selectedSchool) {
            staffGetData(selectedSchool.id);
        }
    }, [ selectedSchool ]);

    useEffect(() => {
        if (selectedSchool) {
            staffGetData(selectedSchool.id);
            staffSetUpdate(false);
        }
    }, [ isUpdate ]);

    const staffColumns: ColumnsType<any> = [
        {
            title: 'Имя',
            dataIndex: 'name',
            key: 'name',
            width: 120,
            sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: 180,
            sorter: (a, b) => (a.email || '').localeCompare(b.email || ''),
        },
        {
            title: 'Чем занимается',
            dataIndex: 'role',
            key: 'role',
            width: 120,
            sorter: (a, b) => (a.role || '').localeCompare(b.role || ''),
        },
        {
            title: 'UTM метки',
            dataIndex: 'utm_labels',
            key: 'utm_labels',
            render: (text, record) => {
                const utmItems = [
                    { label: 'utm_source', value: record.utm_source },
                    { label: 'utm_medium', value: record.utm_medium },
                    { label: 'utm_campaign', value: record.utm_campaign },
                    { label: 'utm_term', value: record.utm_term },
                    { label: 'utm_content', value: record.utm_content },
                    { label: 'utm_group', value: record.utm_group },
                ].filter(item => item.value && item.value !== 'Доступны все метки');

                if (utmItems.length === 0) return null;

                const visibleItems = utmItems.slice(0, MAX_VISIBLE_ITEMS);
                const hiddenCount = utmItems.length - MAX_VISIBLE_ITEMS;

                return (
                    <div className={styles.utmCell}>
                        {visibleItems.map((item, idx) => (
                            <div key={idx}>{item.label} = {item.value}</div>
                        ))}
                        {hiddenCount > 0 && (
                            <Tooltip
                                title={
                                    <div style={{ maxWidth: 'none', whiteSpace: 'nowrap' }}>
                                        {utmItems.map((item, idx) => (
                                            <div key={idx}>{item.label} = {item.value}</div>
                                        ))}
                                    </div>
                                }
                                placement="top"
                                overlayStyle={{ maxWidth: 'none' }}
                                overlayInnerStyle={{ maxWidth: 'none' }}
                            >
                                <span style={{ color: '#1890ff', cursor: 'pointer' }}>
                                    +{hiddenCount} ещё
                                </span>
                            </Tooltip>
                        )}
                    </div>
                );
            },
            width: 200,
            sorter: (a, b) => (a.utm_source || '').localeCompare(b.utm_source || ''),
        },
        {
            title: 'Рекл. кабинеты',
            dataIndex: 'ads',
            key: 'ads',
            width: 120,
            render: (value: string) => renderCellWithLimit(value, styles.cellWithLimit),
            sorter: (a, b) => (a.ads || '').localeCompare(b.ads || ''),
        },
        {
            title: 'Предложения',
            dataIndex: 'offers',
            key: 'offers',
            width: 120,
            render: (value: string) => {
                // Заменяем "Доступны все Предложения" и "Доступны все предложения" на "Доступны все"
                const displayValue = value?.replace(/Доступны все [Пп]редложения/g, 'Доступны все');
                return renderCellWithLimit(displayValue, styles.cellWithLimit);
            },
            sorter: (a, b) => (a.offers || '').localeCompare(b.offers || ''),
        },
        {
            title: 'Контакты',
            dataIndex: 'contact',
            key: 'contact',
            width: 100,
            render: (value: string) => renderCellWithLimit(value, styles.cellWithLimit),
            sorter: (a, b) => (a.contact || '').localeCompare(b.contact || ''),
        },
        {
            title: 'Настройки',
            dataIndex: 'setting',
            key: 'setting',
            width: 100,
            render: (value: string) => renderCellWithLimit(value, styles.cellWithLimit),
            sorter: (a, b) => (a.setting || '').localeCompare(b.setting || ''),
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 80,
            render: (value, record) => (
                <Space size={4}>
                    <Tooltip title="Редактировать">
                        <EditOutlined
                            style={{ 
                                fontSize: 14, 
                                color: 'rgba(0, 0, 0, 0.65)', 
                                cursor: 'pointer',
                                padding: 4
                            }}
                        onClick={() => {
                            setEditUserId(record.id);
                            setEditModalVisible(true);
                        }}
                    />
                    </Tooltip>

                    <Tooltip title="Удалить">
                        <DeleteOutlined
                            style={{ 
                                fontSize: 14, 
                                color: 'rgba(0, 0, 0, 0.65)', 
                                cursor: 'pointer',
                                padding: 4
                            }}
                        onClick={() => {
                            if (selectedSchool) {
                                Modal.confirm({
                                    title: 'Вы уверены что хотите удалить сотрудника?',
                                    icon: <ExclamationCircleOutlined/>,
                                    okText: 'Да',
                                    okType: 'danger',
                                    cancelText: 'Нет',
                                    onOk() {
                                        staffDeleteUser(parseInt(selectedSchool.id), record.id);
                                    },
                                });
                            }
                        }}
                    />
                    </Tooltip>
                </Space>
            )
        },
    ];

    const addStaffMemberBtn = () => {
        if (isAccessEnded()) return false;

        return (
            <div data-help-id="settings-staff-add">
                <Button
                    type="primary"
                    icon={<PlusOutlined/>}
                    onClick={() => setAddModalVisible(true)}
                    style={{
                        marginTop: 20,
                        backgroundColor: '#ff4d4f',
                        borderColor: '#ff4d4f',
                        color: '#fff',
                        borderRadius: '5px',
                    }}
                >
                    Добавить сотрудника
                </Button>
            </div>
        );
    };
    return (
        <MainLayout pageTitle="Сотрудники">
            <div className="page-wrapper">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, padding: '0 24px' }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'baseline',
                        fontSize: '20px',
                        fontWeight: 'bold' 
                    }}>
                        <div>Сотрудники</div>
                        <button
                            data-help-id="settings-staff-help"
                            onClick={() => {
                                setDrawerLink('https://doc.scananalytics.ru/nastrojka/sotrudniki-i-roli/');
                                setAppReportOpen(true);
                            }}
                            style={{
                                border: 'none',
                                outline: 'none',
                                minWidth: 20,
                                gap: 10,
                                background: 'transparent',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                color: 'red',
                                fontSize: 14,
                                cursor: 'pointer',
                                marginLeft: 10
                            }}
                        >
                            <QuestionOutlined 
                                style={{ 
                                    background: 'red', 
                                    color: 'white', 
                                    borderRadius: '50%', 
                                    padding: 5, 
                                    fontSize: 12 
                                }}
                            />
                            <span>Как работать с этим разделом</span>
                        </button>
                    </div>
                    {addStaffMemberBtn()}
                </div>
                {isAccessEnded() ? (
                    <iframe
                        className="access-denied-iframe"
                        src={`https://doc.scananalytics.ru/oshibki-i-preduprezhdeniya/sotrudnik-ne-vidit-otchety/?timestamp=${new Date().getTime()}`}
                    />
                ) : (
                    <>
                <div className="page-content">
                    <div ref={filtersRef} style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Text
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => {
                                if (showFilters) {
                                    resetFilters();
                                }
                                setShowFilters(!showFilters);
                            }}
                        >
                            {showFilters ? 'Очистить и скрыть фильтры' : 'Показать фильтры'}
                        </Text>
                    </div>

                    {showFilters && (
                        <Card 
                            size="small" 
                            style={{ marginBottom: 24, border: '1px solid #d9d9d9', borderRadius: '8px' }}
                        >
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                <Row gutter={[16, 16]}>
                                    <Col span={4}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Имя</Text>
                                            <Select
                                                value={filters.name || undefined}
                                                onChange={(value) => handleFilterChange('name', value || '')}
                                                placeholder="Выберите имя"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueValues.name.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={5}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Email</Text>
                                            <Select
                                                value={filters.email || undefined}
                                                onChange={(value) => handleFilterChange('email', value || '')}
                                                placeholder="Выберите email"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueValues.email.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={5}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Чем занимается</Text>
                                            <Select
                                                value={filters.role || undefined}
                                                onChange={(value) => handleFilterChange('role', value || '')}
                                                placeholder="Выберите роль"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueValues.role.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={5}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Рекламные кабинеты</Text>
                                            <Select
                                                value={filters.ads || undefined}
                                                onChange={(value) => handleFilterChange('ads', value || '')}
                                                placeholder="Выберите кабинет"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                optionLabelProp="label"
                                            >
                                                {uniqueValues.ads.map(v => (
                                                    <Select.Option key={v} value={v} label={v}>
                                                        <Tooltip title={v} placement="right">
                                                            <div style={{ maxWidth: 300 }}>{v}</div>
                                                        </Tooltip>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Space>
                                    </Col>
                                    <Col span={5}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Предложения</Text>
                                            <Select
                                                value={filters.offers || undefined}
                                                onChange={(value) => handleFilterChange('offers', value || '')}
                                                placeholder="Выберите предложение"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                optionLabelProp="label"
                                            >
                                                {uniqueValues.offers.map(v => (
                                                    <Select.Option key={v} value={v} label={v}>
                                                        <Tooltip title={v} placement="right">
                                                            <div style={{ maxWidth: 300 }}>{v}</div>
                                                        </Tooltip>
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </Space>
                                    </Col>
                                </Row>
                                <Row gutter={[16, 16]}>
                                    <Col span={4}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>utm_source</Text>
                                            <Select
                                                value={filters.utm_source || undefined}
                                                onChange={(value) => handleFilterChange('utm_source', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueValues.utm_source.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={4}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>utm_medium</Text>
                                            <Select
                                                value={filters.utm_medium || undefined}
                                                onChange={(value) => handleFilterChange('utm_medium', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueValues.utm_medium.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={4}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>utm_campaign</Text>
                                            <Select
                                                value={filters.utm_campaign || undefined}
                                                onChange={(value) => handleFilterChange('utm_campaign', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueValues.utm_campaign.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={4}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>utm_term</Text>
                                            <Select
                                                value={filters.utm_term || undefined}
                                                onChange={(value) => handleFilterChange('utm_term', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueValues.utm_term.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={4}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>utm_content</Text>
                                            <Select
                                                value={filters.utm_content || undefined}
                                                onChange={(value) => handleFilterChange('utm_content', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueValues.utm_content.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={4}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>utm_group</Text>
                                            <Select
                                                value={filters.utm_group || undefined}
                                                onChange={(value) => handleFilterChange('utm_group', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueValues.utm_group.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                </Row>
                                <Row gutter={[16, 16]}>
                                    <Col span={4}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Контакты</Text>
                                            <Select
                                                value={filters.contact || undefined}
                                                onChange={(value) => handleFilterChange('contact', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueValues.contact.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    <Col span={4}>
                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            <Text>Настройки</Text>
                                            <Select
                                                value={filters.setting || undefined}
                                                onChange={(value) => handleFilterChange('setting', value || '')}
                                                placeholder="Выберите"
                                                allowClear
                                                showSearch
                                                filterOption={(input, option) =>
                                                    (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                                                }
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                options={uniqueValues.setting.map(v => ({ value: v, label: v }))}
                                            />
                                        </Space>
                                    </Col>
                                    {(filters.name || filters.email || filters.role || filters.ads || filters.offers || 
                                      filters.utm_source || filters.utm_medium || filters.utm_campaign || filters.utm_term || 
                                      filters.utm_content || filters.utm_group || filters.contact || filters.setting) && (
                                        <Col span={4}>
                                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                                <Text>&nbsp;</Text>
                                                <Button 
                                                    onClick={resetFilters}
                                                    style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
                                                >
                                                    Очистить фильтры
                                                </Button>
                                            </Space>
                                        </Col>
                                    )}
                                </Row>
                            </Space>
                        </Card>
                    )}

                    <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden' }}>
                    <Table
                        className={styles.information_table}
                        size={'small'}
                        tableLayout={'fixed'}
                        columns={staffColumns}
                        dataSource={filteredData}
                        rowKey="id"
                        loading={{
                            spinning: isLoading,
                            indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />
                        }}
                            scroll={{ x: 1120 }}
                        bordered
                            pagination={{
                                showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                            }}
                        />
                    </div>

                    {/* Дашборд со статистикой */}
                    <div className={styles.dashboard}>
                        <Title level={4} style={{ marginBottom: 16, fontWeight: 500, color: '#595959' }}>Всего сотрудников: <span style={{ color: '#1890ff', fontSize: '28px', fontWeight: 700 }}>{dashboardStats.totalStaff}</span></Title>
                        
                        {/* Все блоки в одном Row - динамический вывод по 3 в ряд */}
                        <Row gutter={[16, 16]}>
                            {/* Ограничения */}
                            <Col span={8}>
                                <Card size="small" className={styles.statCard}>
                                    <div className={styles.statLabel} style={{ marginBottom: 8, fontWeight: 600 }}>Ограничения</div>
                                    <div className={styles.statList}>
                                        <div className={styles.statListItem}>
                                            <span>Доступны контакты клиентов</span>
                                            <span 
                                                className={`${styles.statBadgeGreen} ${styles.statBadgeClickable}`}
                                                onClick={() => {
                                                    resetFilters();
                                                    setShowFilters(true);
                                                    handleFilterChange('contact', 'Доступны');
                                                    scrollToFilters();
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {dashboardStats.contactStats.withContact}
                                            </span>
                                        </div>
                                        <div className={styles.statListItem}>
                                            <span>Не доступны контакты клиентов</span>
                                            <span 
                                                className={`${styles.statBadgeRed} ${styles.statBadgeClickable}`}
                                                onClick={() => {
                                                    resetFilters();
                                                    setShowFilters(true);
                                                    handleFilterChange('contact', 'Не доступны');
                                                    scrollToFilters();
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {dashboardStats.contactStats.withoutContact}
                                            </span>
                                        </div>
                                        <div className={styles.statListItem}>
                                            <span>Есть ограничения по UTM меткам</span>
                                            <span className={styles.statBadgeRed}>{dashboardStats.utmRestricted}</span>
                                        </div>
                                        <div className={styles.statListItem}>
                                            <span>Нет ограничений по UTM меткам</span>
                                            <span className={styles.statBadgeGreen}>{dashboardStats.allUtmAccess}</span>
                                        </div>
                                        <div className={styles.statListItem}>
                                            <span>Доступны все рекламные кабинеты</span>
                                            <span 
                                                className={`${styles.statBadgeGreen} ${styles.statBadgeClickable}`}
                                                onClick={() => {
                                                    resetFilters();
                                                    setShowFilters(true);
                                                    handleFilterChange('ads', 'Доступны все РК');
                                                    scrollToFilters();
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {dashboardStats.allAdsAccess}
                                            </span>
                                        </div>
                                        <div className={styles.statListItem}>
                                            <span>Нет доступа к рекламным кабинетам</span>
                                            <span 
                                                className={`${styles.statBadgeRed} ${styles.statBadgeClickable}`}
                                                onClick={() => {
                                                    resetFilters();
                                                    setShowFilters(true);
                                                    handleFilterChange('ads', 'Нет доступа к РК');
                                                    scrollToFilters();
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {dashboardStats.noAdsAccess}
                                            </span>
                                        </div>
                                        <div className={styles.statListItem}>
                                            <span>Доступны все продажи</span>
                                            <span 
                                                className={`${styles.statBadgeGreen} ${styles.statBadgeClickable}`}
                                                onClick={() => {
                                                    resetFilters();
                                                    setShowFilters(true);
                                                    handleFilterChange('offers', 'Доступны все предложения');
                                                    scrollToFilters();
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {dashboardStats.allOffersAccess}
                                            </span>
                                        </div>
                                        <div className={styles.statListItem}>
                                            <span>Администраторы</span>
                                            <span 
                                                className={`${styles.statBadgeGreen} ${styles.statBadgeClickable}`}
                                                onClick={() => {
                                                    resetFilters();
                                                    setShowFilters(true);
                                                    handleFilterChange('setting', 'Доступны');
                                                    scrollToFilters();
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {dashboardStats.adminStats.admins}
                                            </span>
                                        </div>
                                    </div>
                                </Card>
                            </Col>

                            {/* Чем занимаются */}
                            {dashboardStats.roleStats.length > 0 && (
                                <Col span={8}>
                                    <Card size="small" className={styles.statCard}>
                                        <div className={styles.statLabel} style={{ marginBottom: 8, fontWeight: 600 }}>Чем занимаются</div>
                                        {renderCollapsibleList(dashboardStats.roleStats, 'roleStats', 'role')}
                                    </Card>
                                </Col>
                            )}

                            {/* Рекламные кабинеты */}
                            {dashboardStats.adsStats.filter(item => 
                                !item.label.toLowerCase().includes('доступны все') && 
                                !item.label.toLowerCase().includes('нет доступа')
                            ).length > 0 && (
                                <Col span={8}>
                                    <Card size="small" className={styles.statCard}>
                                        <div className={styles.statLabel} style={{ marginBottom: 8, fontWeight: 600 }}>Рекламные кабинеты</div>
                                        {renderCollapsibleList(
                                            dashboardStats.adsStats.filter(item => 
                                                !item.label.toLowerCase().includes('доступны все') && 
                                                !item.label.toLowerCase().includes('нет доступа')
                                            ),
                                            'adsStats',
                                            'ads'
                                        )}
                                    </Card>
                                </Col>
                            )}

                            {/* UTM метки */}
                            {dashboardStats.utmSourceStats.length > 0 && (
                                <Col span={8}>
                                    <Card size="small" className={styles.statCard}>
                                        <div className={styles.statLabel} style={{ marginBottom: 8, fontWeight: 600 }}>utm_source</div>
                                        {renderCollapsibleList(dashboardStats.utmSourceStats, 'utmSourceStats', 'utm_source')}
                                    </Card>
                                </Col>
                            )}
                            {dashboardStats.utmMediumStats.length > 0 && (
                                <Col span={8}>
                                    <Card size="small" className={styles.statCard}>
                                        <div className={styles.statLabel} style={{ marginBottom: 8, fontWeight: 600 }}>utm_medium</div>
                                        {renderCollapsibleList(dashboardStats.utmMediumStats, 'utmMediumStats', 'utm_medium')}
                                    </Card>
                                </Col>
                            )}
                            {dashboardStats.utmCampaignStats.length > 0 && (
                                <Col span={8}>
                                    <Card size="small" className={styles.statCard}>
                                        <div className={styles.statLabel} style={{ marginBottom: 8, fontWeight: 600 }}>utm_campaign</div>
                                        {renderCollapsibleList(dashboardStats.utmCampaignStats, 'utmCampaignStats', 'utm_campaign')}
                                    </Card>
                                </Col>
                            )}
                            {dashboardStats.utmTermStats.length > 0 && (
                                <Col span={8}>
                                    <Card size="small" className={styles.statCard}>
                                        <div className={styles.statLabel} style={{ marginBottom: 8, fontWeight: 600 }}>utm_term</div>
                                        {renderCollapsibleList(dashboardStats.utmTermStats, 'utmTermStats', 'utm_term')}
                                    </Card>
                                </Col>
                            )}
                            {dashboardStats.utmContentStats.length > 0 && (
                                <Col span={8}>
                                    <Card size="small" className={styles.statCard}>
                                        <div className={styles.statLabel} style={{ marginBottom: 8, fontWeight: 600 }}>utm_content</div>
                                        {renderCollapsibleList(dashboardStats.utmContentStats, 'utmContentStats', 'utm_content')}
                                    </Card>
                                </Col>
                            )}
                            {dashboardStats.utmGroupStats.length > 0 && (
                                <Col span={8}>
                                    <Card size="small" className={styles.statCard}>
                                        <div className={styles.statLabel} style={{ marginBottom: 8, fontWeight: 600 }}>utm_group</div>
                                        {renderCollapsibleList(dashboardStats.utmGroupStats, 'utmGroupStats', 'utm_group')}
                                    </Card>
                                </Col>
                            )}
                        </Row>
                    </div>
                </div>

                <AddModal
                    isVisible={addModalVisible}
                    setVisible={setAddModalVisible}
                />

                <EditModal
                    isVisible={editModalVisible}
                    setVisible={setEditModalVisible}
                    userId={editUserId}
                />
                </>
                )}
            </div>
        </MainLayout>
    );
};

export default Staff;
